import { match } from 'ts-pattern'
import { canRetry } from '@loc/server/utils/music'

import type { MusicMetadataError } from '../../MusicItem/_components/InternalMusicItem/_components/MusicItemError'
import type { MusicMetadataSuccess } from '../../MusicItem/_components/InternalMusicItem/_components/MusicItemSuccess'
import type { MusicMetadata } from '../../MusicItem/_components/InternalMusicItem'
import type {
  MusicDetail,
  MusicListDataItem,
  MusicListDataItemV1,
} from '../../../ai-music-generator/_types/trpc'
import type {
  MusicMetadataInit,
  MusicMetadataProcessing,
} from '../../MusicItem/_components/InternalMusicItem/_components/MusicItemLoading'

export function transformMusicData(
  musicData: MusicListDataItemV1[],
  isSuccess?: boolean,
): MusicMetadata[]
export function transformMusicData(
  musicData: MusicListDataItem,
  isSuccess?: boolean,
): MusicMetadata
export function transformMusicData(
  musicData: MusicListDataItem,
  isSuccess: true,
): MusicMetadataSuccess
export function transformMusicData(
  musicData: MusicListDataItem[],
  isSuccess: true,
): MusicMetadataSuccess[]
export function transformMusicData(
  musicData: MusicListDataItem | MusicListDataItem[],
  isSuccess?: boolean,
) {
  if (Array.isArray(musicData)) {
    return musicData.map((data) => transformMusicData(data, isSuccess))
  }

  if (isSuccess || musicData?.status === 'Success') {
    const musicSuccessData = { ...musicData, status: 'Success' }
    const result: MusicMetadataSuccess = {
      status: 'success',
      numId: musicSuccessData.id,
      isLiked: musicSuccessData.isLiked,
      id: musicSuccessData.uuid,
      src: musicSuccessData.file!,
      musicType: (musicSuccessData as MusicDetail).musicType,
      title: musicSuccessData.title!,
      style: musicSuccessData.style,
      mood: musicSuccessData.mood,
      description:
        (musicSuccessData as MusicListDataItemV1)?.task?.prompt ||
        (musicSuccessData as MusicDetail).labels?.description,
      cover: musicSuccessData.coverFile!,
      duration: musicSuccessData.duration! / 1000,
      author: {
        id: musicSuccessData.user.id,
        name: musicSuccessData.user.name,
        displayName:
          (musicSuccessData as MusicListDataItemV1).user.musicCreator
            ?.displayName || musicSuccessData.user.name,
        avatar: musicSuccessData.user.image,
      },
      coverMusicId: (musicSuccessData as MusicDetail).originMusicId,
      coverAuthor: (musicSuccessData as MusicDetail).originMusicInfo?.user,
      waveformSrc: musicSuccessData.waveformFile!,
      lyrics: musicSuccessData.lyrics!,
      lyricsLrc: musicData.lyricsLrc || '',
      favoriteNum: musicSuccessData.favoriteNum,
      createAt: musicSuccessData.dateCreated!,
      voice:
        (musicSuccessData as MusicListDataItemV1)?.task?.voice ||
        (musicSuccessData as MusicDetail).voice,
      lyricsPrompt:
        (musicSuccessData as MusicListDataItemV1)?.task?.lyricsPrompt ||
        (musicSuccessData as any)?.task?.generationConfig?.lyricsPrompt ||
        (musicSuccessData as MusicDetail)?.subtitle,
    }
    return result
  } else {
    const musicV1Data = { ...musicData } as MusicListDataItemV1
    return match(musicV1Data)
      .with(
        {
          status: 'Processing',
        },
        () => {
          const result: MusicMetadataProcessing = {
            status: 'processing',
            id: musicV1Data.uuid,
          }
          return result
        },
      )
      .with(
        {
          status: 'Failed',
        },
        () => {
          const result: MusicMetadataError = {
            status: 'error',
            allowRetry: canRetry(musicV1Data),
            message: musicV1Data.statusDesc,
            id: musicV1Data.uuid,
            taskId: musicV1Data.taskId,
            title: musicV1Data.title,
            musicType:
              (musicV1Data as any)?.musicType ||
              (musicV1Data as any)?.task?.musicType,
          }
          return result
        },
      )
      .with(
        {
          status: 'Fatal',
        },
        () => {
          const result: MusicMetadataError = {
            status: 'error',
            allowRetry: canRetry(musicV1Data),
            message: musicV1Data.statusDesc,
            id: musicV1Data.uuid,
            taskId: musicV1Data.taskId,
            title: musicV1Data.title,
            musicType:
              (musicV1Data as any)?.musicType ||
              (musicV1Data as any)?.task?.musicType,
          }
          return result
        },
      )
      .otherwise(() => {
        const result: MusicMetadataInit = {
          status: 'init',
          id: musicV1Data.uuid,
        }
        return result
      })
  }
}
